/**
 * features/reports/MarksCompliance.tsx
 * ------------------------------------------------------------------
 * Marks-Entry Compliance (gated canViewProgress). TWO parallel views so
 * CA chasing and exam chasing run side by side:
 *
 *   • CA Marks Compliance   — per (teacher, class, subject), is the chosen
 *     CA's mark entered? Deadline = reportTermStatus.deadlines.ca[label].
 *     The out-of-cycle JUNE cohort (their Term 1) is shown here too but
 *     flagged distinct ("Term 1 · June").
 *   • Exam Marks Compliance — is the EXAM mark entered? Deadline =
 *     reportTermStatus.deadlines.termDeadline. June classes are EXCLUDED
 *     (they have no exam this term).
 *
 * Both deadlines are editable inline here (canManageTerms) — the same
 * fields as Terms → Deadlines, surfaced where the chasing happens.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc } from 'firebase/firestore';
import { ClipboardCheck, Download, AlertTriangle, CheckCircle2, Loader2, Clock, ChevronRight, ChevronDown, FileText, Pencil, Check } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getCaLabels } from '@/lib/assessmentConfig';
import { SCHOOL, termLabel } from '@/lib/school.config';
import { isJuneCohort } from '@/lib/cohort';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildCaCompliance, buildExamCompliance, daysOverdue, type ComplianceRow, type ComplianceStatus } from '@/lib/marksCompliance';
import { buildCompliancePdf } from '@/lib/compliancePdf';
import type { MarksDocument, SubjectMarkEntry } from '@/types';

interface Props {
  year: string;
  term: string;
}

type Mode = 'ca' | 'exam';
type SubTab = 'by-teacher' | 'full-list';

const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_META: Record<ComplianceStatus, { label: string; cls: string }> = {
  complete: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  'not-started': { label: 'Not started', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

export default function MarksCompliance({ year, term: initialTerm }: Props) {
  const { activeStudents, classes, subjects, teachers, assessmentConfigs, termStatuses } = useData();
  const { userData, permissions } = useAuth();
  const canEditDeadline = !!permissions?.canManageTerms;

  const [mode, setMode] = useState<Mode>('ca');
  const [subTab, setSubTab] = useState<SubTab>('by-teacher');
  const [pdfBusy, setPdfBusy] = useState(false);

  const [term, setTerm] = useState(initialTerm);
  const termKeys = useMemo(() => Object.keys(SCHOOL.terms), []);

  const caLabels = useMemo(() => getCaLabels(term, assessmentConfigs), [term, assessmentConfigs]);
  const [caLabel, setCaLabel] = useState('');
  useEffect(() => {
    if (caLabels.length && !caLabels.includes(caLabel)) setCaLabel(caLabels[0]);
  }, [caLabels, caLabel]);

  const termStatus = useMemo(
    () => termStatuses.find((t) => t.year === year && t.term === term),
    [termStatuses, year, term],
  );

  // The assessment being chased + its deadline source.
  const assessmentLabel = mode === 'ca' ? caLabel : 'Exam';
  const deadline = mode === 'ca'
    ? (caLabel ? termStatus?.deadlines?.ca?.[caLabel] : undefined)
    : termStatus?.deadlines?.termDeadline;
  const today = todayISO();
  const overdueDays = deadline ? daysOverdue(deadline, today) : null;
  const deadlinePassed = overdueDays !== null && overdueDays > 0;

  const classById = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);
  const rowIsJune = (r: ComplianceRow) => isJuneCohort(classById.get(r.classId));

  // ---- inline deadline editing ----
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineDraft, setDeadlineDraft] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);
  useEffect(() => { setEditingDeadline(false); setDeadlineDraft(deadline ?? ''); }, [deadline, mode, caLabel, term]);

  async function saveDeadline() {
    if (mode === 'ca' && !caLabel) return;
    setSavingDeadline(true);
    try {
      const id = `${year}_${term}`;
      const deadlines = mode === 'ca'
        ? { ca: { [caLabel]: deadlineDraft } }
        : { termDeadline: deadlineDraft };
      await setDoc(
        doc(db, COLLECTIONS.termStatus, id),
        { id, year, term, deadlines, updatedAt: new Date().toISOString(), updatedBy: userData?.uid },
        { merge: true },
      );
      setEditingDeadline(false);
    } catch (e) {
      console.error('Failed to save deadline:', e);
      alert('Could not save the deadline: ' + (e as Error).message);
    } finally {
      setSavingDeadline(false);
    }
  }

  // ---- marks load (all once, filter client-side) ----
  const [markDocs, setMarkDocs] = useState<MarksDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const snap = await getDocs(collection(db, COLLECTIONS.marks));
        const all = snap.docs.map((d) => d.data() as MarksDocument);
        if (!cancelled) setMarkDocs(all);
      } catch (e) {
        if (!cancelled) { setMarkDocs([]); setLoadError((e as Error).message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const entriesByCS = useMemo(() => {
    const m = new Map<string, Record<string, SubjectMarkEntry>>();
    for (const d of markDocs) {
      if (d.year === year && d.term === term && d.classId && d.subjectId) {
        m.set(`${d.classId}|${d.subjectId}`, d.entries ?? {});
      }
    }
    return m;
  }, [markDocs, year, term]);
  const loadedForTerm = entriesByCS.size;

  // Exam compliance runs over exam-bearing classes only (June excluded).
  const examClasses = useMemo(() => classes.filter((c) => !c.archived && !isJuneCohort(c)), [classes]);

  const rows = useMemo<ComplianceRow[]>(() => {
    const getEntries = (cid: string, sid: string) => entriesByCS.get(`${cid}|${sid}`);
    if (mode === 'exam') {
      return buildExamCompliance({ students: activeStudents, classes: examClasses, subjects, teachers, getEntries });
    }
    if (!caLabel) return [];
    return buildCaCompliance({ caLabel, students: activeStudents, classes, subjects, teachers, getEntries });
  }, [mode, caLabel, activeStudents, classes, examClasses, subjects, teachers, entriesByCS]);

  const incomplete = useMemo(() => rows.filter((r) => r.status !== 'complete'), [rows]);
  const nonCompliant = useMemo(() => (deadlinePassed ? incomplete : []), [deadlinePassed, incomplete]);

  const byTeacher = useMemo(() => {
    const m = new Map<string, ComplianceRow[]>();
    for (const r of rows) {
      const arr = m.get(r.teacherName) ?? [];
      arr.push(r);
      m.set(r.teacherName, arr);
    }
    return [...m.entries()]
      .map(([name, list]) => {
        const incompleteRows = list
          .filter((r) => r.status !== 'complete')
          .sort((a, b) => a.className.localeCompare(b.className) || a.subjectName.localeCompare(b.subjectName));
        return {
          name,
          teacherId: list[0]?.teacherId ?? '',
          completeCount: list.filter((r) => r.status === 'complete').length,
          incompleteRows,
          missingStudents: incompleteRows.reduce((n, r) => n + r.missing.length, 0),
        };
      })
      .sort((a, b) => b.incompleteRows.length - a.incompleteRows.length || a.name.localeCompare(b.name));
  }, [rows]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleTeacher = (name: string) =>
    setExpanded((s) => { const next = new Set(s); next.has(name) ? next.delete(name) : next.add(name); return next; });

  const totalAssignments = rows.length;
  const partialCount = useMemo(() => rows.filter((r) => r.status === 'partial').length, [rows]);
  const notStartedCount = useMemo(() => rows.filter((r) => r.status === 'not-started').length, [rows]);

  const JuneBadge = () => (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Term 1 · June</span>
  );

  function exportCsv() {
    const header = ['Teacher', 'Class', 'Subject', 'Assessment', 'Deadline', 'Days overdue', 'Entered', 'Total', 'Missing', 'Status'];
    const lines = incomplete
      .slice()
      .sort((a, b) => a.teacherName.localeCompare(b.teacherName) || a.className.localeCompare(b.className))
      .map((r) =>
        [r.teacherName, r.className, r.subjectName + (rowIsJune(r) ? ' (Term 1 · June)' : ''), assessmentLabel, deadline ?? '', String(overdueDays ?? ''), String(r.entered), String(r.total), String(r.total - r.entered), STATUS_META[r.status].label]
          .map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','),
      );
    const csv = [header.map((h) => `"${h}"`).join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}-compliance-${assessmentLabel}-${term}-${year}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  async function exportPdf() {
    setPdfBusy(true);
    try {
      const logo = await loadLogoDataUrl();
      const pdf = buildCompliancePdf({
        termLabel: `${mode === 'ca' ? 'CA' : 'Exam'} — ${termLabel(term)}`,
        year,
        caLabel: assessmentLabel,
        deadline, overdueDays, deadlinePassed,
        summary: { total: totalAssignments, complete: totalAssignments - incomplete.length, partial: partialCount, notStarted: notStartedCount, nonCompliant: nonCompliant.length },
        teachers: byTeacher.filter((t) => t.incompleteRows.length > 0).map((t) => ({ name: t.name, incompleteRows: t.incompleteRows, missingStudents: t.missingStudents })),
        logoDataUrl: logo,
        generatedBy: userData?.displayName || undefined,
      });
      pdf.save(`${mode}-compliance-${assessmentLabel}-${term}-${year}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white p-6">
        <div className="flex items-center gap-3">
          <div className="bg-white/10 rounded-xl p-2"><ClipboardCheck size={22} /></div>
          <div>
            <h1 className="text-xl font-bold">{mode === 'ca' ? 'CA' : 'Exam'} Marks Compliance</h1>
            <p className="text-sm opacity-90">
              {mode === 'ca'
                ? <>Who has (and hasn't) entered <strong>CA</strong> marks by the deadline — {termLabel(term)} {year}. Exam entry is tracked in the Exam tab.</>
                : <>Who has (and hasn't) entered <strong>exam</strong> marks by the deadline — {termLabel(term)} {year}. June-cohort classes have no exam and are excluded here.</>}
            </p>
          </div>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 w-fit">
        {([{ id: 'ca', label: 'CA Marks Compliance' }, { id: 'exam', label: 'Exam Marks Compliance' }] as { id: Mode; label: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setMode(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === t.id ? 'seg-on' : 'seg-off'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Controls + deadline */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Term</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white">
              {termKeys.map((k) => <option key={k} value={k}>{termLabel(k)}</option>)}
            </select>
          </div>
          {mode === 'ca' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Continuous assessment</label>
              <select value={caLabel} onChange={(e) => setCaLabel(e.target.value)} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white">
                {caLabels.length === 0 && <option value="">No CA labels for this term</option>}
                {caLabels.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          {/* Deadline display + inline editor */}
          <div className="flex-1 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Clock size={15} className="text-gray-400" />
              {editingDeadline ? (
                <>
                  <input type="date" value={deadlineDraft} onChange={(e) => setDeadlineDraft(e.target.value)} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white" />
                  <button onClick={saveDeadline} disabled={savingDeadline} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#2d5295] text-white text-xs font-semibold hover:bg-[#1b3a6b] disabled:opacity-50">
                    {savingDeadline ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
                  </button>
                  <button onClick={() => { setEditingDeadline(false); setDeadlineDraft(deadline ?? ''); }} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                </>
              ) : (
                <>
                  {deadline ? (
                    deadlinePassed
                      ? <span className="text-red-600 dark:text-red-300 font-medium">{assessmentLabel} deadline {deadline} passed — {overdueDays} day{overdueDays === 1 ? '' : 's'} ago</span>
                      : <span className="text-gray-600 dark:text-gray-300">{assessmentLabel} deadline {deadline} — {overdueDays === 0 ? 'due today' : `${Math.abs(overdueDays as number)} day${Math.abs(overdueDays as number) === 1 ? '' : 's'} to go`}</span>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">No {mode === 'ca' ? caLabel : 'exam'} deadline set.</span>
                  )}
                  {canEditDeadline && (mode === 'exam' || caLabel) && (
                    <button onClick={() => { setEditingDeadline(true); setDeadlineDraft(deadline ?? ''); }} className="inline-flex items-center gap-1 text-xs text-[#2d5295] dark:text-blue-300 hover:underline">
                      <Pencil size={12} /> {deadline ? 'Edit' : 'Set'} deadline
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs">
          {loadError ? <span className="text-red-600 dark:text-red-300">Could not load marks: {loadError}</span>
            : loading ? <span className="text-gray-400">Loading marks…</span>
            : <span className="text-gray-400">{loadedForTerm} class-subject mark record{loadedForTerm === 1 ? '' : 's'} loaded for {termLabel(term)} {year}{loadedForTerm === 0 ? ' — nothing entered for this term yet.' : '.'}</span>}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 flex items-center gap-2 px-1"><Loader2 size={15} className="animate-spin" /> Loading marks…</div>
      ) : (
        <>
          {/* Summary */}
          <div className={`rounded-2xl border p-5 ${deadlinePassed && nonCompliant.length > 0 ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
            {deadlinePassed ? (
              nonCompliant.length > 0 ? (
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300"><AlertTriangle size={18} /><span className="font-semibold">{nonCompliant.length} of {totalAssignments} assignments incomplete — past deadline ({overdueDays} day{overdueDays === 1 ? '' : 's'} overdue).</span></div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300"><CheckCircle2 size={18} /><span className="font-semibold">All {totalAssignments} assignments complete for {assessmentLabel}.</span></div>
              )
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-300">{incomplete.length} of {totalAssignments} assignments still incomplete for {assessmentLabel}.{deadline ? ' Deadline not yet passed — not flagged as non-compliant.' : ' No deadline set.'}</div>
            )}
          </div>

          {/* Sub-tabs */}
          <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 w-fit">
            {([{ id: 'by-teacher', label: 'By teacher' }, { id: 'full-list', label: 'Full list' }] as { id: SubTab; label: string }[]).map((t) => (
              <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'seg-on' : 'seg-off'}`}>{t.label}</button>
            ))}
          </div>

          {/* Full-list */}
          {subTab === 'full-list' && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white">Incomplete assignments <span className="font-normal text-gray-400">— {incomplete.length} of {totalAssignments}</span></h2>
                <div className="flex items-center gap-2">
                  <button onClick={exportCsv} disabled={incomplete.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"><Download size={14} /> CSV</button>
                  <button onClick={exportPdf} disabled={pdfBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-40">{pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PDF summary</button>
                </div>
              </div>
              {incomplete.length === 0 ? (
                <p className="p-5 text-sm text-gray-500 dark:text-gray-400">Nothing incomplete for {assessmentLabel} — every assignment is complete.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="px-4 py-2.5">Teacher</th><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Subject</th><th className="px-4 py-2.5 text-right">Missing</th>{deadlinePassed && <th className="px-4 py-2.5 text-right">Overdue</th>}<th className="px-4 py-2.5">Status</th>
                    </tr></thead>
                    <tbody>
                      {incomplete.slice().sort((a, b) => a.teacherName.localeCompare(b.teacherName) || a.className.localeCompare(b.className) || a.subjectName.localeCompare(b.subjectName)).map((r) => (
                        <tr key={r.key} className="border-b border-gray-50 dark:border-gray-700/50 last:border-0">
                          <td className={`px-4 py-2.5 font-medium ${r.teacherId ? 'text-gray-900 dark:text-white' : 'text-amber-700 dark:text-amber-300'}`}>{r.teacherName}</td>
                          <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300"><span className="inline-flex items-center gap-1.5">{r.className}{mode === 'ca' && rowIsJune(r) && <JuneBadge />}</span></td>
                          <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{r.subjectName}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-red-600 dark:text-red-300">{r.total - r.entered} of {r.total}</td>
                          {deadlinePassed && <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{overdueDays}d</td>}
                          <td className="px-4 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_META[r.status].cls}`}>{STATUS_META[r.status].label}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* By-teacher */}
          {subTab === 'by-teacher' && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">Teachers <span className="font-normal text-gray-400">— most incomplete first; click to see missing classes &amp; students</span></div>
              {byTeacher.length === 0 ? (
                <p className="p-5 text-sm text-gray-500 dark:text-gray-400">No teaching assignments resolved for this term.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {byTeacher.map((t) => {
                    const isOpen = expanded.has(t.name);
                    const clean = t.incompleteRows.length === 0;
                    return (
                      <div key={t.name}>
                        <button onClick={() => !clean && toggleTeacher(t.name)} disabled={clean} className={`w-full flex items-center gap-3 px-4 py-3 text-left ${clean ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
                          <span className="text-gray-400 w-4 shrink-0">{clean ? '' : isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                          <span className={`font-medium ${t.name === 'Unassigned' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>{t.name}</span>
                          <span className="ml-auto flex items-center gap-2">
                            {clean ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-300"><CheckCircle2 size={14} /> All complete</span>
                            ) : (
                              <>
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">{t.incompleteRows.length} class{t.incompleteRows.length === 1 ? '' : 'es'} missing</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{t.missingStudents} student{t.missingStudents === 1 ? '' : 's'}</span>
                              </>
                            )}
                          </span>
                        </button>
                        {isOpen && !clean && (
                          <div className="px-4 pb-4 pl-11 space-y-3">
                            {t.completeCount > 0 && <p className="text-xs text-gray-400">{t.completeCount} other class{t.completeCount === 1 ? '' : 'es'} complete — not shown.</p>}
                            {t.incompleteRows.map((r) => (
                              <div key={r.key} className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/40">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{r.className} · {r.subjectName}</span>
                                  {mode === 'ca' && rowIsJune(r) && <JuneBadge />}
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_META[r.status].cls}`}>{r.status === 'not-started' ? 'Not started' : `${r.missing.length} of ${r.total} missing`}</span>
                                </div>
                                <div className="px-3 py-2 flex flex-wrap gap-1.5">
                                  {r.missing.map((s) => <span key={s.id} className="inline-flex px-2 py-0.5 rounded-md bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-xs">{s.name}</span>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
